import React, { useEffect, useId, useRef, useState } from 'react'
import {
  AlertCircle, ArrowUp, Check, History, Loader2, Plus, Sparkles, Trash2, X, Ban,
} from 'lucide-react'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { useAssistantStore } from '@/stores/assistantStore'
import { CONFIRM_COPY, confirmFields, formatResetDate, type TranscriptItem } from '@/lib/assistant'
import { isApplePlatform } from '@/lib/platform'

/** The assistant's shortcut as this device writes it. */
export const ASSISTANT_SHORTCUT_LABEL = isApplePlatform() ? '⌘J' : 'Ctrl J'

const SUGGESTIONS = [
  'What’s on the schedule this week?',
  'Which quotes are waiting on a reply?',
  'Who owes us money right now?',
  'Add a note to a contact',
]

/**
 * The in-app assistant (docs/engineering/AI-ASSISTANT.md). Desktop: a card
 * docked beside the sidebar, in the same spot as the phone panel (only one is
 * open at a time), so the page stays usable while it works. Phone: a
 * full-height BottomSheet with the composer in its footer, which clears the
 * home indicator and the keyboard.
 */
export function AssistantPanel() {
  const open = useAssistantStore((s) => s.open)
  const setOpen = useAssistantStore((s) => s.setOpen)
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={<PanelTitle />}
        snapPoints={['full']}
        footer={<Composer autoFocus={false} />}
        ariaLabel="Assistant"
      >
        <PanelBody />
      </BottomSheet>
    )
  }

  if (!open) return null
  return (
    <aside
      aria-label="Assistant"
      className="hidden md:flex flex-col fixed md:left-[calc(var(--sidebar-w,0px)+12px)] z-[35] w-[26rem] ds-tile-solid overflow-hidden animate-popover-in"
      style={{
        bottom: 16,
        height: 'min(720px, calc(100dvh - 32px))',
        borderRadius: 'var(--r-2xl, 40px)',
        backgroundImage: 'radial-gradient(120% 50% at 50% 0%, rgb(var(--lavender-rgb) / 0.20), transparent 70%), radial-gradient(70% 40% at 100% 0%, rgb(var(--sky-rgb) / 0.12), transparent 70%)',
      }}
      data-testid="assistant-panel"
    >
      <div className="flex items-center gap-2 px-5 pt-5 pb-3">
        <PanelTitle />
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close assistant"
          className="ds-btn ds-btn-icon ds-btn-ghost ml-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-2">
        <PanelBody />
      </div>
      <div className="px-4 pt-3 pb-4">
        <Composer autoFocus />
      </div>
    </aside>
  )
}

/**
 * The model's reply as plain paragraphs, with "- " / "• " / "1. " lines as
 * real lists and **bold** as bold. Text only: nothing is parsed as HTML.
 */
function AssistantText({ text }: { text: string }) {
  const blocks: Array<{ list: 'ul' | 'ol' | null; lines: string[] }> = []
  let open = false // the last block can take more lines (no blank line since)
  for (const line of text.split('\n')) {
    if (line.trim() === '') { open = false; continue }
    const bullet = /^\s*[-•*]\s+(.*)$/.exec(line)
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    const kind = bullet ? 'ul' : numbered ? 'ol' : null
    const body = bullet?.[1] ?? numbered?.[1] ?? line
    const last = blocks[blocks.length - 1]
    // List items continue a list across blank lines; paragraphs end at one.
    if (last && kind && last.list === kind) last.lines.push(body)
    else if (last && open && !kind && !last.list) last.lines.push(body)
    else blocks.push({ list: kind, lines: [body] })
    open = true
  }
  return (
    <div className="text-sm leading-relaxed text-white/[0.9] break-words space-y-2.5">
      {blocks.map((b, i) => {
        if (!b.list) return <p key={i} className="whitespace-pre-wrap">{inline(b.lines.join('\n'))}</p>
        const List = b.list
        return (
          <List key={i} className={cn('space-y-1.5 pl-5', List === 'ul' ? 'list-disc marker:text-white/40' : 'list-decimal marker:text-white/50')}>
            {b.lines.map((l, j) => <li key={j}>{inline(l)}</li>)}
          </List>
        )
      })}
    </div>
  )
}

function inline(text: string): React.ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
      : part,
  )
}

function PanelTitle() {
  const newConversation = useAssistantStore((s) => s.newConversation)
  const loadHistory = useAssistantStore((s) => s.loadHistory)
  const busy = useAssistantStore((s) => s.busy)
  const [showHistory, setShowHistory] = useState(false)

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <span className="app-ico tn-lavender app-ico-on shrink-0" aria-hidden="true"><Sparkles /></span>
      <span className="text-[17px] font-semibold tracking-tight text-white truncate">Ask AI</span>
      <div className="ml-auto flex items-center gap-1 relative">
        <button
          type="button"
          className="ds-btn ds-btn-icon ds-btn-ghost"
          aria-label="Past conversations"
          aria-expanded={showHistory}
          onClick={() => {
            if (!showHistory) void loadHistory()
            setShowHistory((v) => !v)
          }}
        >
          <History className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="ds-btn ds-btn-icon ds-btn-ghost"
          aria-label="New conversation"
          disabled={busy}
          onClick={() => {
            newConversation()
            setShowHistory(false)
          }}
        >
          <Plus className="w-4 h-4" />
        </button>
        {showHistory && <HistoryList onClose={() => setShowHistory(false)} />}
      </div>
    </div>
  )
}

function HistoryList({ onClose }: { onClose: () => void }) {
  const history = useAssistantStore((s) => s.history)
  const openConversation = useAssistantStore((s) => s.openConversation)
  const removeConversation = useAssistantStore((s) => s.removeConversation)
  const current = useAssistantStore((s) => s.conversationId)

  return (
    <div
      role="dialog"
      aria-label="Past conversations"
      className="ds-tile absolute right-0 top-10 z-10 w-72 max-h-80 overflow-y-auto p-2"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
    >
      {history.length === 0 ? (
        <p className="px-2 py-3 text-sm text-white/[0.62]">No past conversations yet.</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {history.map((h) => (
            <li key={h.id} className="flex items-center gap-1">
              <button
                type="button"
                className={cn(
                  'flex-1 min-w-0 text-left px-2 py-2 rounded-lg text-sm truncate hover:bg-white/[0.06]',
                  h.id === current ? 'text-white' : 'text-white/[0.72]',
                )}
                onClick={() => {
                  void openConversation(h.id)
                  onClose()
                }}
              >
                {h.title}
              </button>
              <button
                type="button"
                className="ds-btn ds-btn-icon ds-btn-ghost"
                aria-label={`Delete “${h.title}”`}
                onClick={() => void removeConversation(h.id)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function UsageNotice() {
  const usage = useAssistantStore((s) => s.usage)
  const configured = useAssistantStore((s) => s.configured)
  const bridgeOnline = useAssistantStore((s) => s.bridgeOnline)
  if (bridgeOnline === false) {
    return (
      <div className="ds-tile p-3 text-sm text-white/[0.72] flex gap-2" role="status" data-testid="assistant-offline">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>The assistant’s computer is offline right now, so it can’t answer yet. It reconnects on its own once that computer is back on.</span>
      </div>
    )
  }
  if (configured === false) {
    return (
      <div className="ds-tile p-3 text-sm text-white/[0.72] flex gap-2" role="status">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>The assistant isn’t switched on for this workspace yet.</span>
      </div>
    )
  }
  if (!usage) return null
  if (usage.paused) {
    return (
      <div className="ds-tile p-3 text-sm text-white flex gap-2" role="status" data-testid="assistant-paused">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          The assistant is paused for this month’s fair use. It’s back on {formatResetDate(usage.resetsOn)}.
          Your AI front desk keeps answering calls.
        </span>
      </div>
    )
  }
  if (usage.nearLimit && usage.percentUsed !== null) {
    return (
      <div className="ds-tile p-3 text-sm text-white/[0.72] flex gap-2" role="status">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>You’ve used {usage.percentUsed}% of this month’s assistant use. It resets on {formatResetDate(usage.resetsOn)}.</span>
      </div>
    )
  }
  return null
}

function PanelBody() {
  const items = useAssistantStore((s) => s.items)
  const busy = useAssistantStore((s) => s.busy)
  const send = useAssistantStore((s) => s.send)
  const usage = useAssistantStore((s) => s.usage)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' })
  }, [items, busy])

  const waiting = busy && items[items.length - 1]?.type !== 'assistant'

  return (
    <div className="flex flex-col gap-3" aria-live="polite" aria-busy={busy}>
      <UsageNotice />
      {items.length === 0 && (
        <div className="animate-fade-up flex flex-col gap-3 pt-2">
          <p className="text-sm text-white/[0.72]">
            Ask a question about your business, or tell me what to do. I’ll check with you before I text or email a customer, touch money, delete anything or change settings.
          </p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className="ds-chip"
                disabled={busy || usage?.paused}
                onClick={() => void send(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      {groupRepeatedTools(items).map(({ item, count }, i) => (
        <Item key={'id' in item ? item.id : `${item.type}-${i}`} item={item} count={count} />
      ))}
      {waiting && (
        <div className="flex items-center gap-2 text-sm text-white/[0.62]" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> Working…
        </div>
      )}
      <div ref={endRef} />
    </div>
  )
}

/**
 * Back-to-back finished calls of the same tool (the model paging through
 * invoices, say) show as one card with a count instead of a stack of repeats.
 */
function groupRepeatedTools(items: TranscriptItem[]): Array<{ item: TranscriptItem; count: number }> {
  const out: Array<{ item: TranscriptItem; count: number }> = []
  for (const item of items) {
    const last = out[out.length - 1]
    if (item.type === 'tool' && item.status === 'ok' && last?.item.type === 'tool' && last.item.status === 'ok' && last.item.name === item.name) {
      last.count += 1
    } else {
      out.push({ item, count: 1 })
    }
  }
  return out
}

function Item({ item, count = 1 }: { item: TranscriptItem; count?: number }) {
  switch (item.type) {
    case 'user':
      return (
        <div className="self-end max-w-[85%] rounded-2xl rounded-br-md bg-white/[0.10] px-3.5 py-2.5 text-sm text-white whitespace-pre-wrap break-words">
          {item.text}
        </div>
      )
    case 'assistant':
      return <AssistantText text={item.text} />
    case 'error':
      return (
        <div className="flex gap-2 text-sm" style={{ color: 'var(--midnight-blush, #F4B5BD)' }} role="alert">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {item.text}
        </div>
      )
    case 'tool':
      return <ToolCard item={item} count={count} />
    case 'confirm':
      return <ConfirmCard item={item} />
  }
}

function ToolCard({ item, count = 1 }: { item: Extract<TranscriptItem, { type: 'tool' }>; count?: number }) {
  const title = item.title ?? item.name.replace(/_/g, ' ')
  return (
    <div className="ds-tile px-3 py-2 flex items-start gap-2 text-[13px]" data-testid="assistant-tool-card">
      <span className="mt-0.5 shrink-0">
        {item.status === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/[0.62]" />}
        {item.status === 'ok' && <Check className="w-3.5 h-3.5" style={{ color: 'var(--midnight-mint, #A8E6CE)' }} />}
        {item.status === 'error' && <AlertCircle className="w-3.5 h-3.5" style={{ color: 'var(--midnight-blush, #F4B5BD)' }} />}
        {(item.status === 'cancelled' || item.status === 'awaiting') && <Ban className="w-3.5 h-3.5 text-white/[0.62]" />}
      </span>
      <span className="min-w-0">
        <span className="text-white/[0.85]">{title}</span>
        {count > 1 && <span className="text-white/[0.62] tabular-nums"> ×{count}</span>}
        {item.status === 'cancelled' && <span className="text-white/[0.62]"> · not done</span>}
        {item.status === 'error' && item.summary && <span className="block text-white/[0.62]">{item.summary}</span>}
      </span>
    </div>
  )
}

function ConfirmCard({ item }: { item: Extract<TranscriptItem, { type: 'confirm' }> }) {
  const decide = useAssistantStore((s) => s.decide)
  const busy = useAssistantStore((s) => s.busy)
  const copy = CONFIRM_COPY[item.kind]
  const fields = confirmFields(item.input)
  const headingId = useId()

  return (
    <section className="ds-tile p-3.5 flex flex-col gap-3 animate-fade-up" aria-labelledby={headingId} data-testid="assistant-confirm-card">
      <div className="flex items-center gap-2">
        <span className="ds-chip">{copy.badge}</span>
      </div>
      <h3 id={headingId} className="text-sm font-semibold text-white">{item.title}</h3>
      {item.target && <p className="text-[13px] text-white/[0.85] -mt-1.5" data-testid="assistant-confirm-target">{item.target}</p>}
      {fields.length > 0 && (
        <dl className="flex flex-col gap-2 text-[13px]">
          {fields.map((f) => (
            <div key={f.label} className={f.long ? 'flex flex-col gap-1' : 'flex gap-2'}>
              <dt className="text-white/[0.62] shrink-0">{f.label}</dt>
              <dd className={cn('text-white break-words', f.long && 'whitespace-pre-wrap rounded-lg bg-black/40 px-2.5 py-2')}>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="ds-btn ds-btn-sm ds-btn-ghost"
          disabled={busy}
          onClick={() => void decide(item.id, 'cancel')}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ds-btn ds-btn-sm ds-btn-primary"
          disabled={busy}
          onClick={() => void decide(item.id, 'confirm')}
        >
          {item.deciding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {copy.action}
        </button>
      </div>
    </section>
  )
}

function Composer({ autoFocus }: { autoFocus: boolean }) {
  const send = useAssistantStore((s) => s.send)
  const busy = useAssistantStore((s) => s.busy)
  const usage = useAssistantStore((s) => s.usage)
  const configured = useAssistantStore((s) => s.configured)
  const [text, setText] = useState('')
  const ref = useRef<HTMLTextAreaElement>(null)
  const inputId = useId()
  const disabled = Boolean(usage?.paused) || configured === false

  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])

  const submit = () => {
    if (!text.trim() || busy || disabled) return
    void send(text)
    setText('')
  }

  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <label htmlFor={inputId} className="sr-only">Message the assistant</label>
      <textarea
        id={inputId}
        ref={ref}
        rows={2}
        maxLength={4000}
        className="input-field flex-1 min-w-0 resize-none text-base sm:text-sm"
        placeholder={disabled ? 'The assistant is paused' : 'Ask or tell me what to do…'}
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault()
            submit()
          }
        }}
      />
      <button
        type="submit"
        className="ds-btn ds-btn-lg ds-btn-primary shrink-0"
        aria-label="Send to assistant"
        disabled={!text.trim() || busy || disabled}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
      </button>
    </form>
  )
}

/** ⌘J / Ctrl J toggles the panel from anywhere in the app. */
export function useAssistantShortcut() {
  const toggle = useAssistantStore((s) => s.toggle)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'j') {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggle])
}

/** Sidebar row under Phone, styled like it (lavender tile). */
export function AssistantButton({ collapsed = false }: { collapsed?: boolean }) {
  const toggle = useAssistantStore((s) => s.toggle)
  const open = useAssistantStore((s) => s.open)
  return (
    <button
      type="button"
      onClick={toggle}
      aria-expanded={open}
      aria-label={open ? 'Close assistant' : 'Ask AI'}
      data-testid="sidebar-assistant"
      title={collapsed ? `Ask AI (${ASSISTANT_SHORTCUT_LABEL})` : undefined}
      className={cn(
        'group relative flex items-center gap-[11px] h-9 coarse:h-11 px-3 rounded-xl text-[13.5px] font-medium transition-colors duration-200 text-left',
        open
          ? 'nav-pill-active text-white shadow-[0_4px_12px_-6px_rgba(0,0,0,0.5)]'
          : 'text-white/[0.66] hover:text-white hover:bg-white/[0.06]',
        collapsed && 'justify-center px-0',
      )}
    >
      <span className={cn('app-ico tn-lavender', open && 'app-ico-on')} aria-hidden="true"><Sparkles /></span>
      {!collapsed && (
        <>
          <span className="flex-1 truncate">Ask AI</span>
          <kbd className="font-mono text-[11px] leading-5 px-1.5 rounded-md border border-white/[0.16] bg-white/[0.06] text-white/[0.62]">
            {ASSISTANT_SHORTCUT_LABEL}
          </kbd>
        </>
      )}
      {collapsed && (
        <span className="absolute left-full ml-2 px-2 py-1 glass rounded-md text-xs text-ink-100 whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity pointer-events-none z-[21]">
          Ask AI
        </span>
      )}
    </button>
  )
}
